import React from 'react';
import { addDays, format } from 'date-fns';
import { GetServerSideProps } from 'next';
import { useQuery } from 'urql';

import Button from '@/components/Button';
import CardGrid from '@/components/CardGrid';
import CategoriesGrid from '@/components/CategoriesGrid';
import HeroImage from '@/components/layout/HeroImage';
import useIsMobile from '@/hooks/useIsMobile';
import useStore from '@/hooks/useStore';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { ErrorPage } from '@/pages/_error';

import query from './query.graphql';

import styles from './styles.module.scss';

const Offers = () => {
  const { exclusive } = useStore('userStore');
  const { now } = useStore('globals');
  const [isMobile, isLtLarge] = useIsMobile(768, 1200);

  const [{ data, error, fetching }] = useQuery<OfferIndexQuery, OfferIndexQueryVariables>({
    query,
    variables: {
      exclusive,
      expiringSoon: `<${format(addDays(new Date(now), 7), 'yyyy-M-dd')}`,
    },
  });

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  if (!fetching && !data?.entries) {
    return <div>no offers right now</div>;
  }

  const benefitsAndOffersEntry = extractFragment<BenefitsAndOffersEntryFragment>(data?.entry);

  return (
    <div className={styles.offer} data-testid="offers-page">
      <HeroImage image={benefitsAndOffersEntry?.heroImage?.[0]?.url} />
      <div className={styles.inner}>
        <h1>Benefits &amp; Offers</h1>
        <CardGrid
          title="Featured Items"
          cards={data?.featured as (OfferFragment | ProductOfferFragment)[]}
          slider={isMobile}
          small={isMobile}
          whiteText={true}
        />
        <div className={styles.categoriesGrid}>
          <CategoriesGrid
            title={isMobile ? 'Browse categories' : 'Browse offers by category'}
            categories={data?.categories as CategoryFragment[]}
            slider={isLtLarge}
            whiteText={!data?.featured?.length}
          />
        </div>
        <CardGrid
          cards={data?.entries as (OfferFragment | ProductOfferFragment)[]}
          slider={isMobile}
          small={isMobile}
        />
        <div className={styles.browseButton}>
          <Button href="/offers/browse" design="highlight-borders" size="medium">
            Browse all offers
          </Button>
        </div>
        <CardGrid
          title="Last chance to get these offers!"
          cards={data?.ending as (OfferFragment | ProductOfferFragment)[]}
          slider={isMobile}
          small={isMobile}
        />
      </div>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async ({ urqlClient, stores }) => {
    await urqlClient
      .query<OfferIndexQuery, OfferIndexQueryVariables>(query, {
        exclusive: stores.userStore.exclusive,
        expiringSoon: `<${format(addDays(new Date(stores.globals.now), 7), 'yyyy-M-dd')}`,
      })
      .toPromise();

    return { props: undefined };
  });

export default withUrql(Offers);
